import { DoorOpen } from "lucide-react";
import { useEffect, useState } from "react";

import { AppShell } from "@/components/layout/AppShell";
import { RoomCard, RoomCardSkeleton } from "@/components/rooms/RoomCard";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/Button";
import { Card, CardHeader } from "@/components/ui/Card";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { EmptyState } from "@/components/ui/EmptyState";
import { SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { siteOrigin } from "@/env";
import { createRoom, deleteRoom, listRooms } from "@/lib/api/rooms";
import type { Room } from "@/lib/api/types";
import { useAsync } from "@/lib/useAsync";
import { useT } from "@/lib/i18n";

export function RoomsPage() {
  const toast = useToast();
  const t = useT();
  const { data, loading, reload } = useAsync(() => listRooms(), []);
  const [confirming, setConfirming] = useState<Room | null>(null);

  const [deleted, setDeleted] = useState<readonly string[]>([]);
  const rooms = (data ?? []).filter((room) => !deleted.includes(room.id));

  const anyLive = rooms.some((room) => room.activeSince !== null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!anyLive) return;
    const id = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      clearInterval(id);
    };
  }, [anyLive]);

  const copy = (slug: string) => {
    const link = `${siteOrigin()}/${slug}`;
    navigator.clipboard
      .writeText(link)
      .then(() => {
        toast.show(t("rooms.linkCopied"), "success");
      })
      .catch(() => {
        toast.show(link, "info");
      });
  };

  const create = async () => {
    try {
      await createRoom({});
      reload();
      toast.show(t("rooms.created"), "success");
    } catch (error) {
      toast.fail(error);
    }
  };

  const remove = async (room: Room) => {
    try {
      await deleteRoom(room.id);
      setDeleted((current) => [...current, room.id]);
      setConfirming(null);
      reload();
    } catch (error) {
      setConfirming(null);
      toast.fail(error);
    }
  };

  return (
    <AppShell>
      <Seo description="The persistent rooms you host." noindex path="/rooms" title="Your rooms" />
      <Card>
        <CardHeader
          action={
            <Button
              onClick={() => {
                void create();
              }}
              size="sm"
            >
              {t("rooms.new")}
            </Button>
          }
          description={t("rooms.subtitle")}
          title={t("rooms.title")}
        />

        {loading && data === null ? (
          <SkeletonList className="divide-y divide-line" label={t("rooms.loading")}>
            <RoomCardSkeleton />
            <RoomCardSkeleton />
          </SkeletonList>
        ) : rooms.length === 0 ? (
          <EmptyState description={t("rooms.noneBody")} icon={DoorOpen} title={t("rooms.none")} />
        ) : (
          <ul className="divide-y divide-line">
            {rooms.map((room) => (
              <li key={room.id}>
                <RoomCard
                  now={now}
                  onCopyLink={() => {
                    copy(room.slug);
                  }}
                  onDelete={() => {
                    setConfirming(room);
                  }}
                  room={room}
                />
              </li>
            ))}
          </ul>
        )}
      </Card>

      {confirming !== null ? (
        <ConfirmDialog
          confirmLabel={t("rooms.delete")}
          destructive
          message={t("rooms.deleteBody")}
          onCancel={() => {
            setConfirming(null);
          }}
          onConfirm={() => remove(confirming)}
          title={t("rooms.deleteTitle", { title: confirming.title })}
        />
      ) : null}
    </AppShell>
  );
}
